import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangleIcon, ArrowLeftIcon, LockIcon, MailIcon, PenLineIcon, PhoneIcon } from 'lucide-react';
import { DetailCard, FieldList } from '../components/detail/DetailCard';
import { ActivityTimeline } from '../components/detail/ActivityTimeline';
import { LogCommunicationModal } from '../components/communication/LogCommunicationModal';
import { useToast } from '../contexts/ToastContext';
import { realtors } from '../data/realtors';
import { leads } from '../data/leads';
import { preApprovals } from '../data/preApprovals';
import { loans } from '../data/loans';
import { activityLog } from '../data/activityHistory';
import type { CommunicationSubject } from '../types/portal';
export function RealtorDetailPage() {
  const {
    realtorId
  } = useParams<{
    realtorId: string;
  }>();
  const {
    showToast
  } = useToast();
  const [logging, setLogging] = useState(false);
  const realtor = realtors.find(item => item.id === realtorId);
  if (!realtor) {
    return <main className="w-full px-4 py-10 sm:px-6 lg:px-8" data-id="element-882" data-mp="2kxgwqkuhh5j6">
        <div className="mx-auto max-w-lg rounded-2xl border border-navy-100 bg-white p-8 text-center shadow-card" data-id="element-883" data-mp="2fyvuygzl2dsm">
          <AlertTriangleIcon className="mx-auto h-6 w-6 text-warn-600" aria-hidden="true" data-id="element-884" data-mp="2b7dx41suwtbv" />
          <h1 className="mt-3 text-lg font-semibold text-navy-900" data-id="element-885" data-mp="2692lblj048r0">
            Partner not found
          </h1>
          <p className="mt-1 text-sm text-navy-500" data-id="element-886" data-mp="225o62x2vzwem">
            This partner record may have been merged with another account.
          </p>
          <Link to="/realtors" className="mt-5 inline-flex h-11 items-center justify-center rounded-lg bg-navy-800 px-4 text-sm font-semibold text-white hover:bg-navy-900" data-id="element-887" data-mp="1s8rswe21mm3q">
            Back to My Realtors
          </Link>
        </div>
      </main>;
  }
  const locked = realtor.access === 'house-locked';
  const entries = activityLog[realtor.id] ?? [];
  const relatedLeads = leads.filter(lead => lead.source.includes(realtor.name));
  const relatedPreApprovals = preApprovals.filter(item => item.realtor.includes(realtor.name));
  const relatedLoans = loans.filter(loan => loan.realtor.includes(realtor.name));
  const pipelineCount = relatedLeads.length + relatedPreApprovals.length + relatedLoans.length;
  const subject: CommunicationSubject = {
    id: realtor.id,
    name: realtor.name,
    context: `${realtor.company} · ${realtor.partnerType}`,
    phone: realtor.phone,
    email: realtor.email,
    goalCredit: realtor.partnerType === 'Realtor' ? 'your Realtor touches goal' : 'your title & insurance partner touches goal'
  };
  return <main className="w-full px-4 py-5 sm:px-6 sm:py-6 lg:px-8" data-id="element-888" data-mp="1tp5m39y05z9b">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-4" data-id="element-889" data-mp="e9qbgnqir087">
        <Link to="/realtors" className="inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-navy-600 hover:text-navy-900" data-id="element-890" data-mp="iblatmow33jc">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-891" data-mp="aq7okm0iavhv" />
          My Realtors
        </Link>

        <header className="rounded-2xl border border-navy-100 bg-white p-5 shadow-card" data-id="element-892" data-mp="5spq5pk47yo4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between" data-id="element-893" data-mp="kpq8j2gf2h9x">
            <div className="min-w-0" data-id="element-894" data-mp="frooofb62dxe">
              <div className="flex flex-wrap items-center gap-2" data-id="element-895" data-mp="3o6ymhb80wwzd">
                <h1 className="text-2xl font-bold tracking-tight text-navy-900" data-id="element-896" data-mp="3o78d910w7864">
                  {realtor.name}
                </h1>
                <span className="inline-flex items-center rounded-full bg-navy-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-navy-600 ring-1 ring-navy-200" data-id="element-897" data-mp="1rdwn8y4etan">
                  {realtor.partnerType}
                </span>
                {locked ? <span className="inline-flex items-center gap-1 rounded-full bg-navy-800 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white" data-id="element-898" data-mp="35ectqzsexmj0">
                    <LockIcon className="h-3 w-3" aria-hidden="true" data-id="element-899" data-mp="23r0vpxmmmfvb" />
                    House account
                  </span> : null}
              </div>
              <p className="mt-2 text-sm text-navy-700" data-id="element-900" data-mp="2cq5qqtyzbr2q">
                {realtor.company} · {realtor.market}
              </p>
              <p className="mt-1 text-xs text-navy-500" data-id="element-901" data-mp="2hi5wx45n7g7r">
                {realtor.id} ·{' '}
                {realtor.lastTouchLabel ?? 'No communication logged by you'}
              </p>
            </div>

            {locked ? <p className="max-w-xs rounded-xl bg-navy-50 p-3 text-xs font-medium text-navy-600" data-id="element-902" data-mp="2rhocz9ifmhuj">
                Managed by the Reach house team. You can view this record, but
                you can’t claim it or log activity against it.
              </p> : <button type="button" onClick={() => setLogging(true)} className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700" data-id="element-903" data-mp="114p56i6nb0lc">
                <PenLineIcon className="h-4 w-4" aria-hidden="true" data-id="element-904" data-mp="tr7su4xkmlar" />
                Log touch
              </button>}
          </div>
        </header>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-id="element-905" data-mp="ju3hz196ec99">
          <div className="flex flex-col gap-4 lg:col-span-2" data-id="element-906" data-mp="jrqoprxywfgw">
            <DetailCard title="Partnership summary" data-id="element-907" data-mp="1hgizg31lr9wt">
              <FieldList items={[{
              label: 'Partner type',
              value: realtor.partnerType
            }, {
              label: 'Company',
              value: realtor.company
            }, {
              label: 'Market',
              value: realtor.market
            }, {
              label: 'Shared buyers in process',
              value: String(realtor.sharedBuyers)
            }, {
              label: 'Referrals YTD',
              value: String(realtor.referralsYtd)
            }, {
              label: 'Your last touch',
              value: realtor.daysSinceMyTouch === null ? 'Never' : realtor.daysSinceMyTouch === 0 ? 'Today' : `${realtor.daysSinceMyTouch} days ago`
            }, {
              label: 'Other officers active',
              value: realtor.otherOfficersActive === 0 ? 'None' : `${realtor.otherOfficersActive} loan officer${realtor.otherOfficersActive === 1 ? '' : 's'}`
            }, {
              label: 'Record access',
              value: locked ? 'House account — view only' : 'Shared company-wide'
            }]} data-id="element-908" data-mp="1hf7rrs51di0f" />
            </DetailCard>

            <DetailCard title={`Shared pipeline (${pipelineCount})`} data-id="element-909" data-mp="29cv06md99ekg">
              {pipelineCount === 0 ? <p className="text-sm text-navy-500" data-id="element-910" data-mp="24ela2q6e145p">
                  No shared borrowers yet. Log touches and ask for a referral to
                  get the first one moving.
                </p> : <div className="space-y-5" data-id="element-911" data-mp="2ddotxwddpnl9">
                  {relatedLeads.length > 0 ? <PipelineGroup title="Leads" data-id="element-912" data-mp="2saz0qsf2u5an">
                      {relatedLeads.map(lead => <PipelineRow key={lead.id} to={`/leads/${lead.id}`} title={lead.name} detail={`${lead.purpose} · ${lead.amount} · ${lead.program}`} meta={lead.id} data-id="element-913" data-mp="2nc4ipsoxokrl" />)}
                    </PipelineGroup> : null}

                  {relatedPreApprovals.length > 0 ? <PipelineGroup title="Pre-approvals" data-id="element-914" data-mp="hb005w3nlg8m">
                      {relatedPreApprovals.map(item => <PipelineRow key={item.id} to="/pre-approvals" title={item.borrower} detail={`${item.amount} · ${item.program} · ${item.expiresLabel}`} meta={item.id} data-id="element-915" data-mp="lb0pibsajbug" />)}
                    </PipelineGroup> : null}

                  {relatedLoans.length > 0 ? <PipelineGroup title="Active loans" data-id="element-916" data-mp="7knf82rxfl1y">
                      {relatedLoans.map(loan => <PipelineRow key={loan.id} to="/loans" title={loan.borrower} detail={`${loan.amount} · ${loan.milestone} · ${loan.closingLabel}`} meta={loan.id} data-id="element-917" data-mp="cbvrr7ojzmjc" />)}
                    </PipelineGroup> : null}
                </div>}
            </DetailCard>

            <DetailCard title="Activity history" data-id="element-918" data-mp="33g0hoy6woeri">
              <ActivityTimeline entries={entries} data-id="element-919" data-mp="2yhqminl6le5w" />
            </DetailCard>
          </div>

          <div className="flex flex-col gap-4" data-id="element-920" data-mp="2tkgqgp3ae493">
            <DetailCard title="Contact" data-id="element-921" data-mp="2tlrtunstl9zo">
              <ul className="space-y-3 text-sm" data-id="element-922" data-mp="2pibwfp8xf5lx">
                <li className="flex items-center gap-2 text-navy-800" data-id="element-923" data-mp="2pk6hr4o40zqj">
                  <PhoneIcon className="h-4 w-4 shrink-0 text-navy-400" aria-hidden="true" data-id="element-924" data-mp="2kqli786vbxp8" />
                  <a href={`tel:${realtor.phone.replace(/[^0-9]/g, '')}`} className="font-semibold hover:text-brand-700" data-id="element-925" data-mp="2at7fschs59fx">
                    {realtor.phone}
                  </a>
                </li>
                <li className="flex items-center gap-2 text-navy-800" data-id="element-926" data-mp="21t132stwodht">
                  <MailIcon className="h-4 w-4 shrink-0 text-navy-400" aria-hidden="true" data-id="element-927" data-mp="1cdl5o3f6me1f" />
                  <a href={`mailto:${realtor.email}`} className="break-all font-semibold hover:text-brand-700" data-id="element-928" data-mp="17dpcz0bxq916">
                    {realtor.email}
                  </a>
                </li>
              </ul>
              <p className="mt-4 rounded-lg bg-navy-50 px-3 py-2 text-xs text-navy-600" data-id="element-929" data-mp="tgk28n1a0lzl">
                This partner stays on your list as long as you keep logging
                communication against the shared record.
              </p>
            </DetailCard>

            <DetailCard title="Follow-up cadence" data-id="element-930" data-mp="yec0rzwff6cr">
              <p className="text-sm text-navy-700" data-id="element-931" data-mp="ydk8axy4loqp">
                Target: a logged touch every 7 days for active partners.
              </p>
              <p className="mt-2 text-sm font-semibold text-navy-900" data-id="element-932" data-mp="29ujv5e1pfbdc">
                {realtor.daysSinceMyTouch === null ? 'No touch logged yet' : realtor.daysSinceMyTouch >= 7 ? `${realtor.daysSinceMyTouch} days since your last touch — follow-up due` : `${realtor.daysSinceMyTouch === 0 ? 'Touched today' : `${realtor.daysSinceMyTouch} days ago`} — on cadence`}
              </p>
              {!locked ? <button type="button" onClick={() => showToast(`Reminder set to follow up with ${realtor.name} in 7 days.`)} className="mt-4 inline-flex h-11 w-full items-center justify-center rounded-lg border border-navy-200 px-3 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-933" data-mp="304avp7fe9xln">
                  Set a follow-up reminder
                </button> : null}
            </DetailCard>
          </div>
        </div>
      </div>

      <LogCommunicationModal subject={logging ? subject : null} onClose={() => setLogging(false)} onLogged={showToast} data-id="element-934" data-mp="1k7rdavwt0y8" />
    </main>;
}
function PipelineGroup({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-935" data-mp="3or0faxfcauxa">
      <p className="text-xs font-semibold uppercase tracking-wide text-navy-500" data-id="element-936" data-mp="3oqqm70z007nd">
        {title}
      </p>
      <ul className="mt-2 space-y-2" data-id="element-937" data-mp="3eqq4tkkow1lt">{children}</ul>
    </div>;
}
function PipelineRow({
  to,
  title,
  detail,
  meta
}: {
  to: string;
  title: string;
  detail: string;
  meta: string;
}) {
  return <li data-id="element-938" data-mp="350na9fxx6fld">
      <Link to={to} className="block rounded-xl border border-navy-100 p-3 hover:border-brand-200 hover:bg-brand-50/40" data-id="element-939" data-mp="350eyjrdj9gbr">
        <p className="text-sm font-semibold text-navy-900" data-id="element-940" data-mp="31165koqbhmh9">{title}</p>
        <p className="mt-0.5 text-sm text-navy-700" data-id="element-941" data-mp="2vz6yp4vdc7fk">{detail}</p>
        <p className="mt-0.5 text-xs text-navy-500" data-id="element-942" data-mp="2r15koycsuyay">{meta}</p>
      </Link>
    </li>;
}